import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimSplitText } from '@readdy/anim/header/split-text/react';
import { isAnchorNavigating } from '@/lib/scrollOwnership';

type HeroState = {
  img: string;
  alt: string;
  shape: string;
  frame: string;
};

const STATES: HeroState[] = [
  {
    img: 'https://storage.helloreaddy.io/project_files/ae47dfd7-4355-4bd2-920d-0647c7de8b99/3a2c3cf7-92bd-4408-9567-b167f511299b_compressed_ChatGPT-Image-2026916-16_53_23.webp',
    alt: 'Pet owner, dog and veterinarian in a bright clinic',
    shape: '58% 42% 63% 37% / 48% 56% 44% 52%',
    frame: '62% 38% 47% 53% / 46% 58% 42% 54%',
  },
  {
    img: 'https://storage.helloreaddy.io/project_files/ae47dfd7-4355-4bd2-920d-0647c7de8b99/3a66e4f5-6faa-47fe-91a1-43b488623b89_compressed_ChatGPT-Image-2026916-16_53_26.webp',
    alt: 'Veterinarian listening to a golden retriever heartbeat',
    shape: '48% 52% 46% 54% / 60% 40% 60% 40%',
    frame: '52% 48% 60% 40% / 44% 62% 40% 58%',
  },
  {
    img: 'https://storage.helloreaddy.io/project_files/ae47dfd7-4355-4bd2-920d-0647c7de8b99/c763d6a4-69b2-44ec-ab94-66f8b816441b_compressed_ChatGPT-Image-2026916-16_53_53.webp',
    alt: 'Veterinarian comforting a calm cat with owner nearby',
    shape: '68% 32% 52% 48% / 40% 62% 38% 60%',
    frame: '58% 42% 40% 60% / 62% 40% 58% 42%',
  },
];

const WHEEL_THRESHOLD = 55;
const LOCK_MS = 650;

export default function Hero() {
  const [state, setState] = useState(0);
  const [isDesktop, setIsDesktop] = useState(true);
  const location = useLocation();

  const stateRef = useRef(0);
  const lockedRef = useRef(true);       // page scroll is trapped by hero
  const transitioningRef = useRef(false);
  const accumRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => { stateRef.current = state; }, [state]);

  // Preload all hero images
  useEffect(() => {
    STATES.forEach(s => { const i = new Image(); i.src = s.img; });
  }, []);

  useEffect(() => {
    const check = () => {
      const fine = window.matchMedia('(pointer: fine)').matches;
      const wide = window.innerWidth >= 1024;
      setIsDesktop(fine && wide);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      return;
    }

    // Start locked at state 0 — lock is input-only (wheel/touch/key preventDefault).
    // We deliberately do NOT toggle body/html overflow, so the document scrollbar
    // stays present the entire time and viewport width never changes.
    // BUT: if user landed via a hash anchor (e.g. /#services) or with an existing
    // scroll offset, do NOT lock — they're not entering the Hero sequence.
    const hasHash = !!location.hash;
    const alreadyScrolled = window.scrollY > 2;
    if (hasHash || alreadyScrolled) {
      lockedRef.current = false;
      accumRef.current = 0;
      transitioningRef.current = false;
      stateRef.current = STATES.length - 1;
      setState(STATES.length - 1);
    } else {
      lockedRef.current = true;
      window.scrollTo(0, 0);
    }

    const releaseLock = () => {
      lockedRef.current = false;
      accumRef.current = 0;
      transitioningRef.current = false;
    };

    const advance = (dir: 1 | -1) => {
      if (transitioningRef.current) return;
      const cur = stateRef.current;
      const next = cur + dir;
      if (next < 0 || next > STATES.length - 1) return;

      transitioningRef.current = true;
      stateRef.current = next;
      setState(next);
      window.setTimeout(() => { transitioningRef.current = false; }, LOCK_MS);
    };

    const onWheel = (e: WheelEvent) => {
      if (isAnchorNavigating()) return; // never intercept while nav tween is running
      if (!lockedRef.current) return;
      // Hero must only own wheel while the hero section is actually in view
      if (window.scrollY > 2) { lockedRef.current = false; return; }

      const cur = stateRef.current;

      // At final state, downward wheel must immediately hand off to native scroll.
      if (cur === STATES.length - 1 && !transitioningRef.current && e.deltaY > 0) {
        releaseLock();
        return; // do NOT preventDefault — let this very gesture scroll the page
      }

      // At first state, upward wheel is a no-op — don't trap it either
      if (cur === 0 && !transitioningRef.current && e.deltaY < 0) {
        return;
      }

      e.preventDefault();

      const now = performance.now();
      if (now - lastWheelTsRef.current > 180) accumRef.current = 0;
      lastWheelTsRef.current = now;

      if (transitioningRef.current) return;

      accumRef.current += e.deltaY;

      if (accumRef.current >= WHEEL_THRESHOLD) {
        accumRef.current = 0;
        advance(1);
      } else if (accumRef.current <= -WHEEL_THRESHOLD) {
        accumRef.current = 0;
        advance(-1);
      }
    };

    // Touch fallback for touchpads that emit touch events
    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      if (isAnchorNavigating() || !lockedRef.current) return;
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isAnchorNavigating() || !lockedRef.current) return;
      e.preventDefault();
      if (transitioningRef.current) return;
      const y = e.touches[0]?.clientY ?? 0;
      const dy = touchStartY - y;
      if (Math.abs(dy) < 40) return;
      advance(dy > 0 ? 1 : -1);
      touchStartY = y;
    };

    // Re-lock only when the user has scrolled all the way back to the top of the hero
    const onScroll = () => {
      if (isAnchorNavigating()) return;
      if (lockedRef.current) return;
      if (window.scrollY <= 2) {
        lockedRef.current = true;
        accumRef.current = 0;
        stateRef.current = STATES.length - 1;
        setState(STATES.length - 1);
      }
    };

    // Keyboard accessibility
    const onKey = (e: KeyboardEvent) => {
      if (isAnchorNavigating()) return;
      if (!lockedRef.current) return;
      if (['ArrowDown', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); advance(1); }
      else if (['ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); advance(-1); }
    };

    const onRelease = () => {
      lockedRef.current = false;
      accumRef.current = 0;
      transitioningRef.current = false;
    };
    window.addEventListener('hero:release', onRelease);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('hero:release', onRelease);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
    };
  }, [isDesktop]);

  const active = STATES[state];

  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ background: '#F2D16B' }}
    >
      <div className="relative min-h-screen overflow-hidden">
        <span className="deco-float-lg absolute pointer-events-none hidden md:block" style={{ width: 720, height: 720, top: -260, left: -220, background: '#F8E4A8', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <span className="deco-float-sm-alt absolute pointer-events-none hidden md:block" style={{ width: 520, height: 520, bottom: -220, right: -140, background: '#EFC148', borderRadius: '52% 48% 39% 61% / 58% 44% 56% 42%', opacity: 0.6 }} aria-hidden />

        <div className="relative max-w-[1440px] mx-auto pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-5 px-5 md:px-12 lg:pl-16 lg:pr-8">
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-cocoa">Modern veterinary care</div>
              <h1 className="font-display mt-7 font-bold leading-[1.02] text-cocoa text-[40px] md:text-[52px] lg:text-[60px] max-w-none">
                <AnimSplitText stagger={50}>
                  <span className="block whitespace-nowrap">Care that feels good</span>
                </AnimSplitText>
                <AnimSplitText stagger={50}>
                  <span className="block whitespace-nowrap">for both of you.</span>
                </AnimSplitText>
              </h1>
              <p className="mt-7 text-[17px] leading-relaxed text-ink/80 max-w-[440px]">
                Thoughtful, modern veterinary care for pets and the people who love them.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-7">
                <Link
                  to="/book"
                  className="btn-motion inline-flex items-center justify-center gap-2 h-[54px] px-8 rounded-full bg-cocoa hover:bg-careDark text-white text-[15px] font-semibold cursor-pointer whitespace-nowrap"
                >
                  Book an Appointment
                  <i className="ri-arrow-right-line text-[17px]"></i>
                </Link>
                <Link
                  to="/services"
                  className="group inline-flex items-center gap-2 text-[15px] font-semibold text-cocoa hover:text-careDark cursor-pointer whitespace-nowrap transition-colors border-b border-cocoa/50 pb-1"
                >
                  Explore our services
                  <i className="link-arrow-icon ri-arrow-right-line text-[16px]"></i>
                </Link>
              </div>

              {isDesktop && (
                <div className="mt-10 flex items-center gap-3" aria-hidden>
                  {STATES.map((_, i) => (
                    <span
                      key={i}
                      className="block h-[6px] rounded-full transition-all duration-500"
                      style={{
                        width: i === state ? 30 : 10,
                        background: i === state ? '#5A3B1E' : 'rgba(90,59,30,0.28)',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-7 px-5 md:px-12 lg:px-0">
              <div className="relative w-full aspect-[5/5] lg:aspect-[6/5]">
                <span
                  className="absolute -inset-6 md:-inset-8"
                  style={{
                    background: '#FFF3D2',
                    borderRadius: active.frame,
                    transition: 'border-radius 650ms cubic-bezier(.22,1,.36,1)',
                  }}
                  aria-hidden
                />
                <div
                  className="relative w-full h-full overflow-hidden bg-line"
                  style={{
                    borderRadius: active.shape,
                    transition: 'border-radius 650ms cubic-bezier(.22,1,.36,1)',
                  }}
                >
                  {STATES.map((s, i) => (
                    <img
                      key={i}
                      src={s.img}
                      alt={i === state ? active.alt : ''}
                      aria-hidden={i !== state}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      style={{
                        opacity: i === state ? 1 : 0,
                        transform: i === state ? 'scale(1)' : 'scale(1.04)',
                        transition: 'opacity 600ms cubic-bezier(.22,1,.36,1), transform 650ms cubic-bezier(.22,1,.36,1)',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {isDesktop && (
          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-8 flex flex-col items-center gap-2 text-cocoa/80 transition-opacity duration-500"
            style={{ opacity: state < STATES.length - 1 ? 1 : 0 }}
            aria-hidden
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">Scroll</span>
            <span className="relative w-[22px] h-[34px] rounded-full border-[1.5px] border-cocoa/70 flex justify-center">
              <span className="hero-scroll-dot absolute top-[6px] w-[3px] h-[6px] rounded-full bg-cocoa/80" />
            </span>
          </div>
        )}
      </div>
    </section>
  );
}